/**
 * Shared module for handling browser back/forward button navigation warnings
 * when there are active recordings.
 * 
 * This module exports a function that components can use to check for active
 * recordings. The actual blocking is done by the individual pages using
 * Next.js's beforePopState or other mechanisms.
 */

type HasActiveRecordingCheck = () => boolean;

// Registry of check functions
const activeRecordingChecks = new Set<HasActiveRecordingCheck>();

/**
 * Register a function that checks if there's an active recording.
 * Returns an unregister function.
 */
export function registerActiveRecordingCheck(check: HasActiveRecordingCheck): () => void {
  activeRecordingChecks.add(check);
  
  return () => {
    activeRecordingChecks.delete(check);
  };
}

/**
 * Check if any registered component has an active recording.
 * Exported so pages can use this in their own navigation handling.
 */
export function hasAnyActiveRecording(): boolean {
  for (const check of activeRecordingChecks) {
    try {
      if (check()) {
        return true;
      }
    } catch {
      // Ignore errors in check functions
    }
  }
  return false;
}
